import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from './client';

// ===== Types =====

export interface CapacityRole {
  id: number;
  name: string;
  code: string | null;
}

export interface CapacityNeed {
  id: number;
  roleId: number;
  role: CapacityRole;
  month: string;
  fte: number;
}

export interface CapacityAssignment {
  id: number;
  userId: number;
  user: { id: number; firstName: string | null; lastName: string | null };
  roleId: number;
  role: CapacityRole;
  month: string;
  fte: number;
  isRoleMismatch?: boolean;
  isOverallocated?: boolean;
}

/**
 * MKS-358 — an assignment is either a booking or a plan. `tentative` grants no
 * project access and is shown apart from `confirmed` everywhere the planner
 * displays allocation.
 */
export type AssignmentStatus = 'confirmed' | 'tentative';

/**
 * MKS-358 — the hours-and-per-cent half of every allocation payload, mirroring
 * `allocationFigureProperties` in `backend/src/api/schemas/capacity.ts`.
 * Hours are hours per month; percentages are on a 0..100 scale and measure the
 * share of THIS person's month capacity (so a half-timer booked solid reads
 * 100 %, not 50 %).
 */
export interface AllocationFigures {
  availableHours: number;
  allocatedHours: number;
  allocatedPercent: number;
  confirmedHours: number;
  confirmedPercent: number;
  tentativeHours: number;
  tentativePercent: number;
}

export interface AvailablePerson extends AllocationFigures {
  id: number;
  firstName: string | null;
  lastName: string | null;
  availableFte: number;
  allocatedFte: number;
  remainingFte: number;
  workingDays: number;
  timeOffDays: number;
  maxFte: number;
  hasRoleTag: boolean;
}

export interface OverviewRole {
  roleId: number | null;
  roleName: string;
  roleCode: string | null;
  needed: number;
  assigned: number;
  // MKS-358 — `assigned` is the two together; the planner shows how much of
  // the cover is only penciled in.
  confirmed: number;
  tentative: number;
  gap: number;
}

export interface OverviewMonth {
  month: string;
  totalNeeded: number;
  totalAssigned: number;
  totalConfirmed: number;
  totalTentative: number;
  gap: number;
  status: 'full' | 'partial' | 'empty';
  roles: OverviewRole[];
}

export interface MatrixAssignment {
  id: number;
  projectId: number;
  projectName: string | null;
  roleId: number | null;
  roleName: string;
  roleCode: string | null;
  fte: number;
  // MKS-358 — the assignment behind the cell: which project assignment it is,
  // whether it is booked or only planned, and the allocation in both units.
  assignmentId: number;
  status: AssignmentStatus;
  share: number;
  hours: number;
}

export interface MatrixMonth extends AllocationFigures {
  workingDays: number;
  timeOffDays: number;
  /** The person's own FTE for THIS month — the tooltip figure, never primary. */
  fte: number;
  availableFte: number;
  allocatedFte: number;
  remainingFte: number;
  isOverallocated: boolean;
  assignments: MatrixAssignment[];
}

export interface MatrixPerson {
  id: number;
  firstName: string | null;
  lastName: string | null;
  roleTags: CapacityRole[];
  months: Record<string, MatrixMonth>;
}

export interface PlanningMatrix {
  months: string[];
  people: MatrixPerson[];
}

export interface HiringGap {
  roleId: number;
  roleName: string;
  roleCode: string | null;
  month: string;
  totalNeeded: number;
  totalAssigned: number;
  totalAvailable: number;
  gap: number;
  projects: { projectId: number; projectName: string | null; needed: number }[];
}

// ===== API Functions =====

export async function fetchProjectNeeds(projectId: number, fromMonth?: string, toMonth?: string) {
  const params = new URLSearchParams();
  if (fromMonth) params.set('fromMonth', fromMonth);
  if (toMonth) params.set('toMonth', toMonth);
  const query = params.toString() ? `?${params}` : '';
  return apiGet<{ data: CapacityNeed[] }>(`/capacity/projects/${projectId}/needs${query}`);
}

export async function upsertProjectNeeds(projectId: number, needs: { roleId: number; month: string; fte: number }[]) {
  return apiPut<{ data: CapacityNeed[] }>(`/capacity/projects/${projectId}/needs`, { needs });
}

export async function fetchProjectAssignments(projectId: number, fromMonth?: string, toMonth?: string) {
  const params = new URLSearchParams();
  if (fromMonth) params.set('fromMonth', fromMonth);
  if (toMonth) params.set('toMonth', toMonth);
  const query = params.toString() ? `?${params}` : '';
  return apiGet<{ data: CapacityAssignment[] }>(`/capacity/projects/${projectId}/assignments${query}`);
}

export async function createCapacityAssignment(projectId: number, data: { userId: number; roleId: number; month: string; fte: number }) {
  return apiPost<{ data: CapacityAssignment }>(`/capacity/projects/${projectId}/assignments`, data);
}

export async function updateCapacityAssignment(id: number, fte: number) {
  return apiPatch<{ data: CapacityAssignment }>(`/capacity/assignments/${id}`, { fte });
}

export async function deleteCapacityAssignment(id: number) {
  return apiDelete(`/capacity/assignments/${id}`);
}

// ===== Planner gap-fill and confirm (MKS-358) =====

/**
 * The allocation to put on the assignment: either a share of the person's own
 * FTE (`0 < share <= 1`) or hours per month. The backend derives the other,
 * so exactly one is sent.
 */
export type AssignmentAllocationInput = { share: number } | { hours: number };

export interface FillCapacityNeedInput {
  roleId: number;
  month: string;
  userId: number;
  status: AssignmentStatus;
  allocation?: AssignmentAllocationInput;
}

export interface FillCapacityNeedResult {
  assignmentId: number;
  outcome: 'created' | 'extended';
  status: AssignmentStatus;
  startDate: string | null;
  endDate: string | null;
  billingRate: number | null;
  billingRateCurrency: string;
  roleId: number | null;
  roleName: string | null;
}

/**
 * Fill a role-month gap by putting the person on a project assignment —
 * creating one, or extending the one they already hold in that role to cover
 * the month. The planner's only write path for staffing once
 * `mks_358_planner_allocation_ui` is on.
 */
export async function fillCapacityNeed(projectId: number, input: FillCapacityNeedInput) {
  return apiPost<{ data: FillCapacityNeedResult }>(`/capacity/projects/${projectId}/fill-need`, input);
}

export interface ConfirmedAssignment {
  id: number;
  userId: number;
  projectId: number;
  status: AssignmentStatus;
  roleName: string | null;
  allocationShare: number | null;
  allocationHours: number | null;
}

/**
 * Turn a tentative assignment into a confirmed one. The route carries the
 * whole request in its path, so there is no body to send.
 */
export async function confirmProjectAssignment(projectId: number, assignmentId: number) {
  return apiPost<{ data: ConfirmedAssignment }>(
    `/projects/${projectId}/assignments/${assignmentId}/confirm`,
    undefined,
  );
}

export async function fetchAvailablePeople(roleId: number, month: string, includeAll = false) {
  return apiGet<{ data: AvailablePerson[] }>(`/capacity/available-people?roleId=${roleId}&month=${month}&includeAll=${includeAll}`);
}

export async function fetchProjectOverview(projectId: number) {
  return apiGet<{ data: OverviewMonth[] }>(`/capacity/projects/${projectId}/overview`);
}

export async function fetchPlanningMatrix(fromMonth?: string, toMonth?: string) {
  const params = new URLSearchParams();
  if (fromMonth) params.set('fromMonth', fromMonth);
  if (toMonth) params.set('toMonth', toMonth);
  const query = params.toString() ? `?${params}` : '';
  return apiGet<{ data: PlanningMatrix }>(`/capacity/matrix${query}`);
}

export async function fetchHiringGaps(fromMonth?: string, toMonth?: string) {
  const params = new URLSearchParams();
  if (fromMonth) params.set('fromMonth', fromMonth);
  if (toMonth) params.set('toMonth', toMonth);
  const query = params.toString() ? `?${params}` : '';
  return apiGet<{ data: HiringGap[] }>(`/capacity/hiring-gaps${query}`);
}

export async function shiftProjectPlan(projectId: number, direction: 1 | -1) {
  return apiPost<{ data: { success: boolean } }>(`/capacity/projects/${projectId}/shift`, { direction });
}

export interface ProjectCapacitySummary {
  projectId: number;
  projectName: string | null;
  months: {
    month: string;
    totalNeeded: number;
    totalAssigned: number;
    gap: number;
    status: string;
    roles: { roleName: string; roleCode: string | null; needed: number; assigned: number; gap: number; people: string[] }[];
  }[];
}

export async function fetchProjectsCapacitySummary(fromMonth?: string, toMonth?: string) {
  const params = new URLSearchParams();
  if (fromMonth) params.set('fromMonth', fromMonth);
  if (toMonth) params.set('toMonth', toMonth);
  const query = params.toString() ? `?${params}` : '';
  return apiGet<{ data: ProjectCapacitySummary[] }>(`/capacity/projects-summary${query}`);
}

// ===== User Role Tags =====

export interface RoleTagMatrix {
  users: { id: number; firstName: string | null; lastName: string | null; email: string }[];
  roles: CapacityRole[];
  tags: { userId: number; roleId: number }[];
}

export async function fetchRoleTagMatrix() {
  return apiGet<{ data: RoleTagMatrix }>('/user-role-tags/matrix');
}

export async function toggleRoleTag(userId: number, roleId: number) {
  return apiPost<{ data: { userId: number; roleId: number; active: boolean } }>('/user-role-tags/toggle', { userId, roleId });
}

export async function fetchUserRoleTags(userId: number) {
  return apiGet<{ data: { role: CapacityRole }[] }>(`/user-role-tags/${userId}`);
}

export async function setUserRoleTags(userId: number, roleIds: number[]) {
  return apiPut<{ data: { role: CapacityRole }[] }>(`/user-role-tags/${userId}`, { roleIds });
}
